// src/components/About.js
import React from "react";
import styled from "styled-components";
import MissionImage from "../assets/images/mission.jpeg";
import ExpertiseImage from "../assets/images/expertise.jpeg";
import ProcessImage from "../assets/images/process.webp";

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: #f8fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #0f172a;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #2563eb 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const AboutGrid = styled.div`
  display: grid;
  gap: 4rem;
`;

const AboutCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(148, 163, 184, 0.1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  &:nth-child(even) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2rem;
    text-align: center;

    &:nth-child(even) {
      grid-template-columns: 1fr;
    }
  }
`;

const AboutImageContainer = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.1),
      rgba(118, 75, 162, 0.1)
    );
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  transition: transform 0.3s ease;
  display: block;

  ${AboutImageContainer}:hover & {
    transform: scale(1.05);
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AboutCardTitle = styled.h3`
  font-size: 2rem;
  color: #0f172a;
  margin-bottom: 0.5rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #475569;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  color: #475569;

  &::before {
    content: "✓";
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: bold;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 4rem;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1.5rem;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #64748b;
  font-weight: 500;
`;

const aboutData = [
  {
    id: 1,
    title: "Our Mission",
    text: "At ISO Audit Pro, our unwavering commitment is to empower accredited inspection and materials testing laboratories. We specialize in ISO assessments and quality management system consulting. Our mission is clear: to elevate your organization's performance.",
    features: [
      "Superior service and expert guidance",
      "Efficient solutions for quality goals",
      "Targeted training for key personnel",
      "Sustainable excellence in operations",
    ],
    image: MissionImage,
    altText: "ISO Audit Pro mission - Professional team working together on quality management system consulting and ISO accreditation goals",
  },
  {
    id: 2,
    title: "Our Expertise",
    text: "With over 30 years of industry experience, ISO Audit Pro stands as a beacon of excellence. Our seasoned team specializes in ISO assessments and quality management system consulting for inspection and testing laboratories.",
    features: [
      "30+ years of industry experience",
      "ISO assessments and consulting",
      "Dual perspective as accredited agency",
      "Trusted partner for success",
    ],
    image: ExpertiseImage,
    altText: "ISO 17025 audit expertise - Senior consultant reviewing laboratory documentation for compliance assessment",
  },
  {
    id: 3,
    title: "Our Process",
    text: "At ISO Audit Pro, our process is meticulously crafted to ensure excellence. It begins with a systematic review of a company's internal workflows, dissecting every facet to identify areas for improvement.",
    features: [
      "Systematic workflow review",
      "ISO standards alignment",
      "Collaborative goal setting",
      "Continuous improvement focus",
    ],
    image: ProcessImage,
    altText: "AISC compliance workflow - Quality management process diagram showing systematic approach to accreditation",
  },
];

const About = () => {
  return (
    <AboutSection id="about">
      <Container>
        <SectionHeader>
          <SectionTitle>About ISOAuditPro</SectionTitle>
          <SectionSubtitle>
            We are your trusted partner in achieving and maintaining the highest
            standards while propelling your success in the competitive
            landscape.
          </SectionSubtitle>
        </SectionHeader>

        <AboutGrid>
          {aboutData.map((item, index) => (
            <AboutCard key={item.id}>
              <AboutImageContainer>
                <AboutImage src={item.image} alt={item.altText} />
              </AboutImageContainer>
              <AboutContent>
                <AboutCardTitle>{item.title}</AboutCardTitle>
                <AboutText>{item.text}</AboutText>
                <FeatureList>
                  {item.features.map((feature, featureIndex) => (
                    <FeatureItem key={featureIndex}>{feature}</FeatureItem>
                  ))}
                </FeatureList>
              </AboutContent>
            </AboutCard>
          ))}
        </AboutGrid>

        <StatsContainer>
          <StatItem>
            <StatNumber>30+</StatNumber>
            <StatLabel>Years of Experience</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>500+</StatNumber>
            <StatLabel>Successful Audits</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>98%</StatNumber>
            <StatLabel>Client Satisfaction</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>Support Available</StatLabel>
          </StatItem>
        </StatsContainer>
      </Container>
    </AboutSection>
  );
};

export default About;
